/**
 * Equipment kinds: which parts of a unit each KIND of equipment has.
 *
 * ONE table, read by the Details page (which rows to show), the Edit window (which sections to
 * show), plate-proof reads ("no plate" vs "does not apply") and FSR prefill (which grid rows to
 * seed), so they can never disagree about what an ATS-only unit or a fire pump consists of.
 *
 * CONTRACT: a kind decides which rows a screen PROMPTS for, never which rows it may hide. A unit that already holds data for a part
 * shows that part whatever its kind says: use `componentsToShow` (the kind's list unioned with the parts that hold data).
 *
 * The kind itself is stored in the AppSheet column "Generator Type" (one writer, AppSheet first).
 * This file only READS that value: `equipmentKindForType` maps every spelling measured on
 * 2026-10-10 (11 spellings, 43% blank) to a kind, and anything it cannot place to "unrecorded".
 * It never guesses a kind from other columns.
 */
import { UNIT_COMPONENT_TYPES, type UnitComponentType } from './registry';

export const EQUIPMENT_KIND_IDS = [
  "standbyLiquid",
  "standbyAir",
  "portable",
  "rental",
  "firePump",
  "atsOnly",
  "batteryStorage",
  "otherPump",
  "otherEquipment",
  "unrecorded",
] as const;
export type EquipmentKindId = (typeof EQUIPMENT_KIND_IDS)[number];

export interface EquipmentKindDef {
  readonly label: string;
  /** The "Generator Type" value stored for this kind. Absent for "unrecorded" (blank). */
  readonly storedType?: string;
  /** Parts this kind has, in display order. */
  readonly components: readonly UnitComponentType[];
}

const SET = ["generator", "engine", "alternator", "ats"] as const;

export const EQUIPMENT_KINDS: Readonly<Record<EquipmentKindId, EquipmentKindDef>> = {
  standbyLiquid: { label: "Standby generator, liquid-cooled", storedType: "Liquid-Cooled Standby Gen", components: [...SET, "ats2"] },
  standbyAir: { label: "Standby generator, air-cooled", storedType: "Air-Cooled Standby Gen", components: [...SET, "ats2"] },
  portable: { label: "Portable generator", storedType: "Portable Customer Gen", components: ["generator", "engine", "alternator"] },
  rental: { label: "Rental generator", storedType: "Rental Gen", components: ["generator", "engine", "alternator"] },
  firePump: { label: "Fire pump", storedType: "Fire Pump", components: ["firePump", "controller", "engine"] },
  atsOnly: { label: "ATS only", storedType: "ATS Only", components: ["ats", "ats2"] },
  batteryStorage: { label: "Battery storage system", storedType: "Battery Storage System", components: ["other"] },
  otherPump: { label: "Other pump", storedType: "Pump", components: ["other"] },
  otherEquipment: { label: "Other equipment", storedType: "Other", components: ["other"] },
  // Not a kind: the state of a unit nobody has typed. It keeps the generator-set rows so a unit
  // that already shows them today does not lose any.
  unrecorded: { label: "Not yet recorded", components: [...SET, "ats2"] },
};

/** Every spelling seen in "Generator Type" that is not a kind's own stored value. */
const ALIASES: Readonly<Record<string, EquipmentKindId>> = { "ats": "atsOnly" };

const BY_STORED: ReadonlyMap<string, EquipmentKindId> = new Map(
  EQUIPMENT_KIND_IDS.flatMap((id) => {
    const t = EQUIPMENT_KINDS[id].storedType;
    return t ? [[t.toLowerCase(), id] as const] : [];
  }),
);

/**
 * The kind for a stored "Generator Type" value. Blank, unknown and ambiguous spellings (the
 * measured "Standby", which could be liquid- or air-cooled) are "unrecorded": never a guess.
 *
 * @param {unknown} raw The stored value.
 * @return {EquipmentKindId} The kind.
 */
export function equipmentKindForType(raw: unknown): EquipmentKindId {
  if (typeof raw !== "string") return "unrecorded";
  const key = raw.trim().toLowerCase();
  if (BY_STORED.has(key)) return BY_STORED.get(key) as EquipmentKindId;
  return Object.prototype.hasOwnProperty.call(ALIASES, key) ? ALIASES[key] : "unrecorded";
}

/**
 * Parts a stored "Generator Type" value has.
 *
 * @param {unknown} raw The stored value.
 * @return {readonly UnitComponentType[]} The parts, in display order.
 */
export function componentsForType(raw: unknown): readonly UnitComponentType[] {
  return EQUIPMENT_KINDS[equipmentKindForType(raw)].components;
}

/**
 * The parts a screen shows: the kind's own list, then every other part that already holds data (never hide a held value).
 *
 * @param {unknown} raw The stored "Generator Type" value.
 * @param {Iterable<UnitComponentType>} holdingData Parts the unit record or AppSheet columns already hold values for.
 * @return {UnitComponentType[]} The parts, kind order first, then held parts in registry order.
 */
export function componentsToShow(raw: unknown, holdingData: Iterable<UnitComponentType>): UnitComponentType[] {
  const base = [...componentsForType(raw)];
  const held = new Set(holdingData);
  return [...base, ...UNIT_COMPONENT_TYPES.filter((t) => held.has(t) && !base.includes(t))];
}
